import { ConsolidationRelationshipResponse } from "@common/schemas/domains/accounting/consolidation.schema";
import { ConsolidationEntityRelationshipType } from "@common/types/domains/accounting/consolidation";
import { isActiveRelationship } from "./ownershipGraph.helpers";

export function getActiveOwnershipRelationships(
    relationships: ConsolidationRelationshipResponse[],
): ConsolidationRelationshipResponse[] {
    return relationships.filter(
        (relationship) =>
            isActiveRelationship(relationship) &&
            relationship.type === ConsolidationEntityRelationshipType.ownership,
    );
}

export function getConflictingOwnershipRelationships({
    sourceEntityUuid,
    targetEntityUuid,
    relationships,
}: {
    sourceEntityUuid: string;
    targetEntityUuid: string;
    relationships: ConsolidationRelationshipResponse[];
}): ConsolidationRelationshipResponse[] {
    return getActiveOwnershipRelationships(relationships).filter(
        (relationship) =>
            (relationship.targetEntityUuid === targetEntityUuid &&
                relationship.sourceEntityUuid !== sourceEntityUuid) ||
            (relationship.sourceEntityUuid === targetEntityUuid &&
                relationship.targetEntityUuid === sourceEntityUuid),
    );
}

export function isOwnershipDescendant({
    ancestorUuid,
    descendantUuid,
    relationships,
    excludedRelationshipIds,
}: {
    ancestorUuid: string;
    descendantUuid: string;
    relationships: ConsolidationRelationshipResponse[];
    excludedRelationshipIds: Set<string>;
}): boolean {
    const childrenByParentUuid = new Map<string, string[]>();
    for (const relationship of getActiveOwnershipRelationships(relationships)) {
        if (excludedRelationshipIds.has(relationship.id)) {
            continue;
        }
        const children =
            childrenByParentUuid.get(relationship.sourceEntityUuid) ?? [];
        children.push(relationship.targetEntityUuid);
        childrenByParentUuid.set(relationship.sourceEntityUuid, children);
    }

    const stack = [ancestorUuid];
    const visitedUuids = new Set<string>();
    while (stack.length > 0) {
        const currentUuid = stack.pop();
        if (currentUuid === undefined) {
            break;
        }
        if (currentUuid === descendantUuid) {
            return true;
        }
        if (visitedUuids.has(currentUuid)) {
            continue;
        }
        visitedUuids.add(currentUuid);
        for (const childUuid of childrenByParentUuid.get(currentUuid) ?? []) {
            stack.push(childUuid);
        }
    }
    return false;
}
